import React, {useCallback, useEffect, useState} from 'react'
import {Link, useLocation} from '@tanstack/react-router'
import {HiBars3, HiShoppingBag} from 'react-icons/hi2'
import {useDebounceCallback} from 'usehooks-ts'
import {useTranslations} from 'use-intl'
import clsx from 'clsx'
import {DesignCollectionMenu, ProductDepartmentMenu} from '~/components/navigation/DesktopMenus'
import PromotionBanner from '~/components/navigation/PromotionBanner'
import useBasketOverlayState from '~/hooks/useBasketOverlayState'
import BasketOverlay from '~/components/basket/BasketOverlay'
import MobileMenu from '~/components/navigation/MobileMenu'
import useLocaleParams from '~/hooks/useLocaleParams'
import {useBasketQuery} from '~/hooks/useBasket'
import {useIsMinLarge} from '~/hooks/useScreen'
import Logo from '~/components/images/Logo'

type SubMenu = 'collections' | 'products'

const Navigation = () => {
    const params = useLocaleParams()
    const t = useTranslations('navigation')
    const [isMobileMenuVisible, setIsMobileMenuVisible] = useState<boolean>(false)
    const {isBasketOverlayVisible, showBasketOverlay, hideBasketOverlay} = useBasketOverlayState()
    const [visibleSubMenu, setVisibleSubMenu] = useState<SubMenu | undefined>(undefined)
    const setVisibleSubMenuDebounced = useDebounceCallback(setVisibleSubMenu, 150)

    const location = useLocation()
    const isDesktop = useIsMinLarge()

    const {data: basket} = useBasketQuery()
    const basketQuantity = basket?.basketItems.reduce((prev, item) => prev + item.quantity, 0) ?? 0

    const closeMenu = useCallback(() => setIsMobileMenuVisible(false), [])

    useEffect(() => {
        closeMenu()
    }, [location.pathname])

    return (
        <>
            <PromotionBanner />
            <nav className="sticky z-50 inset-0">
                <div className="backdrop-blur-xl bg-black/30 transform-gpu border-b border-white/5">
                    <div className="mx-auto max-w-screen-xl flex items-center gap-14 justify-between px-4 sm:px-6">
                        <div className="flex flex-row items-center justify-between gap-16">
                            <Link to="/$lang" params={params} className="max-w-[200px] block py-4">
                                <Logo className="w-full h-full" />
                                <span className="sr-only">{t('goHome')}</span>
                            </Link>
                            {isDesktop && (
                                <>
                                    <ul className="hidden lg:flex flex-row gap-4">
                                        <li className="group relative">
                                            <Link
                                                to="/$lang/heart-or-pass"
                                                params={params}
                                                onClick={closeMenu}
                                                className="inline-block font-display font-medium tracking-wider uppercase text-sm px-2 py-4 border-b-2 border-b-transparent hover:border-b-nova bg-gradient-to-t hover:from-nova/20 to-10% to-transparent"
                                            >
                                                {t('menu.perfectFit')}
                                            </Link>
                                        </li>
                                        <li className="group relative">
                                            <Link
                                                to="/$lang/collections"
                                                params={params}
                                                onClick={closeMenu}
                                                aria-expanded={visibleSubMenu === 'collections'}
                                                aria-haspopup="true"
                                                className={clsx(
                                                    'inline-block font-display font-medium tracking-wider uppercase text-sm px-2 py-4 border-b-2 border-b-transparent hover:border-b-nova bg-gradient-to-t hover:from-nova/20 to-10% to-transparent',
                                                    visibleSubMenu === 'collections'
                                                        ? '!border-b-nova !from-nova/20'
                                                        : ''
                                                )}
                                                onMouseEnter={() => {
                                                    setVisibleSubMenu('collections')
                                                    setVisibleSubMenuDebounced('collections')
                                                }}
                                                onMouseLeave={() =>
                                                    setVisibleSubMenuDebounced(undefined)
                                                }
                                                onFocus={() => {
                                                    setVisibleSubMenu('collections')
                                                    setVisibleSubMenuDebounced('collections')
                                                }}
                                                onBlur={() => setVisibleSubMenuDebounced(undefined)}
                                            >
                                                {t('menu.designCollections')}
                                            </Link>
                                        </li>
                                        <li className="group relative">
                                            <Link
                                                to="/$lang/products"
                                                params={params}
                                                onClick={closeMenu}
                                                aria-expanded={visibleSubMenu === 'products'}
                                                aria-haspopup="true"
                                                className={clsx(
                                                    'inline-block font-display font-medium tracking-wider uppercase text-sm px-2 py-4 border-b-2 border-b-transparent hover:border-b-nova bg-gradient-to-t hover:from-nova/20 to-10% to-transparent',
                                                    visibleSubMenu === 'products'
                                                        ? '!border-b-nova !from-nova/20'
                                                        : ''
                                                )}
                                                onMouseEnter={() => {
                                                    setVisibleSubMenu('products')
                                                    setVisibleSubMenuDebounced('products')
                                                }}
                                                onMouseLeave={() =>
                                                    setVisibleSubMenuDebounced(undefined)
                                                }
                                                onFocus={() => {
                                                    setVisibleSubMenu('products')
                                                    setVisibleSubMenuDebounced('products')
                                                }}
                                                onBlur={() => setVisibleSubMenuDebounced(undefined)}
                                            >
                                                {t('menu.products')}
                                            </Link>
                                        </li>
                                    </ul>
                                    <DesignCollectionMenu
                                        isVisible={visibleSubMenu === 'collections'}
                                        closeMenu={() => setVisibleSubMenuDebounced(undefined)}
                                        openMenu={() => {
                                            setVisibleSubMenu('collections')
                                            setVisibleSubMenuDebounced('collections')
                                        }}
                                    />
                                    <ProductDepartmentMenu
                                        isVisible={visibleSubMenu === 'products'}
                                        closeMenu={() => setVisibleSubMenuDebounced(undefined)}
                                        openMenu={() => {
                                            setVisibleSubMenu('products')
                                            setVisibleSubMenuDebounced('products')
                                        }}
                                    />
                                </>
                            )}
                        </div>
                        <div className="flex items-center justify-center gap-4 sm:gap-6 py-4">
                            <div className="relative flex items-center justify-center w-6 h-6">
                                {isDesktop ? (
                                    <>
                                        <button
                                            type="button"
                                            className="relative p-4"
                                            onClick={showBasketOverlay}
                                        >
                                            <HiShoppingBag aria-hidden="true" className="w-5 h-5" />
                                            <span className="sr-only">{t('basket')}</span>
                                            {basketQuantity > 0 && (
                                                <span className="absolute p-px flex items-center justify-center bg-comet rounded-full min-w-4 h-4 border border-comet top-2 right-2 text-xs scale-90">
                                                    <span className="sr-only">
                                                        {t('basketItemQuantity')}:
                                                    </span>
                                                    <span>{basketQuantity}</span>
                                                </span>
                                            )}
                                        </button>
                                        {isBasketOverlayVisible && (
                                            <BasketOverlay
                                                className="absolute z-20 -right-4 top-full"
                                                onClose={hideBasketOverlay}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <Link
                                        to="/$lang/basket"
                                        params={params}
                                        className="relative p-4"
                                    >
                                        <HiShoppingBag aria-hidden="true" className="w-5 h-5" />
                                        <span className="sr-only">{t('basket')}</span>
                                        {basketQuantity > 0 && (
                                            <span className="absolute p-px flex items-center justify-center bg-comet rounded-full min-w-4 h-4 border border-comet top-2 right-2 text-xs scale-90">
                                                <span className="sr-only">
                                                    {t('basketItemQuantity')}:
                                                </span>
                                                <span>{basketQuantity}</span>
                                            </span>
                                        )}
                                    </Link>
                                )}
                            </div>
                            <div className="flex items-center justify-center w-6 h-6 lg:hidden">
                                <button
                                    type="button"
                                    onClick={() => setIsMobileMenuVisible(true)}
                                    className="p-4"
                                >
                                    <HiBars3 aria-hidden="true" className="w-6 h-6" />
                                    <span className="sr-only">{t('openMenu')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="nav-gradient-line" aria-hidden="true" />
            </nav>
            {!isDesktop && <MobileMenu isVisible={isMobileMenuVisible} onClose={closeMenu} />}
        </>
    )
}

export default Navigation
